﻿import React, { useState, useMemo, useCallback } from "react";
import Workflow from "./Workflow/Workflow";
import ArtifactBlock from "./Artifact/ArtifactBlock";
import { mapArtifacts, mapWorkflows } from "./ArtifactMapDefinitions";
import {
  selectLifecyclePopupOpen,
  selectLifecycleDetails,
} from "@/features/homepage/workflowManagerSlice";
import { useDispatch, useSelector } from "react-redux";

const ArtifactMap = ({ workflows, selectedConnection, selectedWorkflow }) => {
  const lifecyclePopupOpen = useSelector(selectLifecyclePopupOpen);
  const workflowsForLifecycle = useSelector(selectLifecycleDetails);

  const selectedWorkflowsList = [
    ...(workflowsForLifecycle?.workflowsCompleted?.map(
      (workflow) => workflow.operationId
    ) || []),
    ...(workflowsForLifecycle?.workflowsUpcoming?.map(
      (workflow) => workflow.operationId
    ) || []),
  ];

  const initialArtifactStates = useMemo(
    () =>
      mapArtifacts.reduce((acc, artifact) => {
        acc[artifact.name] = "default";
        return acc;
      }, {}),
    []
  );

  const [artifactStates, setArtifactStates] = useState(initialArtifactStates);

  const initialWorkflowStates = useMemo(
    () =>
      mapWorkflows.reduce((acc, workflow) => {
        acc[workflow.operationId] = "default";
        return acc;
      }, {}),
    []
  );

  const [workflowStates, setWorkflowStates] = useState(initialWorkflowStates);

  const getConnectionCount = (artifact) => {
    const targetWorkflows = workflows.filter((workflow) =>
      artifact.targetWorkflowOperationIds.includes(workflow.OperationId)
    );

    const sourceWorkflows = workflows.filter((workflow) =>
      artifact.sourceWorkflowOperationIds.includes(workflow.OperationId)
    );

    const getConnectionsList = (workflows, connectionType) => {
      if (workflows.length === 1) {
        // shortcut the simple case
        return workflows[0][connectionType].map((connection) => ({
          ConnectionId: connection.ConnectionId,
        }));
      }

      // Collect all ConnectionIds
      const allConnections = workflows.flatMap((workflow) =>
        workflow[connectionType].map((connection) => ({
          ConnectionId: connection.ConnectionId,
        }))
      );

      // Deduplicate by ConnectionId
      const dedupedConnections = [
        ...new Map(allConnections.map((c) => [c.ConnectionId, c])).values(),
      ];

      return dedupedConnections;
    };

    const sourceConnectionList = getConnectionsList(
      sourceWorkflows,
      "SourceConnections"
    );
    const targetConnectionList = getConnectionsList(
      targetWorkflows,
      "TargetConnections"
    );

    //Combines sourceConnectionList + targetConnectionList and removes duplicates. Then gives the number of unique connections for the artifact.
    const totalConnections = [
      ...(sourceConnectionList || []),
      ...(targetConnectionList || []),
    ].reduce((map, conn) => map.set(conn.ConnectionId, conn), new Map()).size;

    return totalConnections;
  };

  const handleWorkflowInteraction = (
    sourceName,
    targetName,
    operationId,
    state
  ) => {
    setArtifactStates((prevStates) => {
      const newStates = { ...prevStates };

      if (newStates[sourceName] !== state) {
        newStates[sourceName] = state;
      }

      if (newStates[targetName] !== state) {
        newStates[targetName] = state;
      }

      return newStates;
    });
  };

  const memoizedSetArtifactState = useCallback((artifactName, newState) => {
    setArtifactStates((prevStates) => {
      if (prevStates[artifactName] !== newState) {
        return {
          ...prevStates,
          [artifactName]: newState,
        };
      }
      return prevStates;
    });
  }, []);

  return (
    <>
      {mapArtifacts.map((artifact) => (
        <ArtifactBlock
          key={artifact.name}
          name={artifact.name}
          alignment={artifact.alignment}
          connectionCount={getConnectionCount(artifact)}
          x={artifact.x}
          y={artifact.y}
          selectedWorkflow={
            lifecyclePopupOpen && selectedWorkflowsList.length !== 0
              ? selectedWorkflowsList
              : [selectedWorkflow]
          }
          artifactState={artifactStates[artifact.name]}
          associatedWorkflowOperationIds={[
            ...artifact.targetWorkflowOperationIds,
            ...artifact.sourceWorkflowOperationIds,
          ]}
          setArtifactState={(newState) =>
            memoizedSetArtifactState(artifact.name, newState)
          }
        />
      ))}

      {mapWorkflows.map((workflow, i) => (
        <Workflow
          key={i}
          source={workflow.source}
          target={workflow.target}
          startX={workflow.startX}
          startY={workflow.startY}
          endX={workflow.endX}
          endY={workflow.endY}
          label={workflow.label}
          turns={workflow.turns}
          notTrueWorkflow={workflow.notTrueWorkflow}
          statusBoxAlignment={workflow.statusBoxAlignment}
          direction={workflow.direction}
          selectedWorkflow={selectedWorkflow}
          workflows={workflows.find(
            (x) => x.OperationId === workflow.operationId
          )}
          operationId={workflow.operationId}
          workflowState={workflowStates[workflow.operationId]}
          onInteraction={handleWorkflowInteraction}
        />
      ))}
    </>
  );
};

export default ArtifactMap;
